import React, {FC, useState} from 'react'
import {useSelector} from "react-redux"
import MetaTags from "react-meta-tags"
import {Field} from "formik"
import * as Yup from "yup"
import {ValidationError} from "yup"
import cn from 'classnames'
import {useAppContext} from "../../AppContext"
import getLocalizeString from "../../utils/getLocalizeString"
import usePassValidate from '../../hooks/usePassValidate'
import useValidatedFields from "../../hooks/useValidatedFields"
import useFormActions from "../../hooks/useFormActions"
import Form from '../Form'
import Input from "../Input"
import Captcha from "../Captcha"
import Button from "../Button"

const Register: FC = () => {
  const initFields = {
    login: '',
    email: '',
    password: '',
    agreement: false,
    agreementDataProcessing: false
  }
  const initValidators = {
    login: Yup.string()
      .required('Field can not be empty')
      .min(3, 'Username should contain at least 3 characters.')
      .matches(/^[-a-zA-Z0-9_\.@]+$/, 'Username is invalid.'),
    email: Yup.string()
      .required('Field can not be empty')
      .email('Email is not a valid email address.'),
    password: Yup.string()
      .required('Field can not be empty')
      .min(8)
      .matches(/(?=.*\d).*$/, 'numbersError')
      .matches(/(?=.*[A-z]).*$/, 'lettersError'),
    agreement: Yup.boolean()
      .test(
        'is-agree',
        '',
        (value) => {
          setAgreeCheck(value)
          return !!value
        }),
    agreementDataProcessing: Yup.boolean()
      .test(
        'is-agree-data-processing',
        '',
        (value) => {
          setAgreeCheckDataProcessing(value)
          return !!value
        })
    
  }
  const {validatePass, passValidators, setPassValidators} = usePassValidate()
  const {formFields, formValidators} = useValidatedFields(initFields, initValidators)
  const {backendValidate, submitRegister, handleResult} = useFormActions()
  const {appType} = useAppContext()
  const [agreeCheck, setAgreeCheck] = useState<boolean | undefined>(true)
  const [agreeCheckDataProcessing, setAgreeCheckDataProcessing] = useState<boolean | undefined>(true)
  const showCaptcha = useSelector(({reducer}: any) => reducer.showCaptcha)

  const registerValidation = async (values: any) => {
    const errors: any = {}
    const schema = Yup.object().shape(formValidators)

    await schema.validate(values, {abortEarly: false})
      .then(() => setPassValidators({}))
      .catch((err) => {
        validatePass(err)

        errors.login = err.inner.find((e: ValidationError) => e.path === 'login')?.message
        errors.email = err.inner.find((e: ValidationError) => e.path === 'email')?.message
        errors.password = err.inner.find((e: ValidationError) => e.path === 'password')?.message
      })

    return errors
  }

  return (
    <>
      <MetaTags>
        <title>{getLocalizeString('Sign up ')}</title>
      </MetaTags>

      <Form
        title={'Sign up '}
        withSocial
        initFields={formFields}
        validate={(values: any) => registerValidation(values)}
        nav={[
          {
            link: '/login',
            title: 'Log in '
          },
          {
            link: '/register',
            title: 'Sign up'
          }
        ]}
        onSubmit={(values, actions) => {
          backendValidate(values)
            .then(res => {
              handleResult(res, actions)
              if ( !res.status ) {
                actions?.setFieldError('login', res.msg.username)
                actions?.setFieldError('email', res.msg.email)
                actions?.setFieldError('password', res.msg.password)
                actions?.setSubmitting(false)
              } else {
                submitRegister(values, actions)
                  .finally(() => actions?.setSubmitting(false))
              }
            })
        }}
      >
        {({isSubmitting}) => {
          return (
            <>
              <div className={'form-block'}>
                <Input
                  name={'login'}
                  placeholder={'Username'}
                  variant={'login'}
                />
              </div>

              <div className={'form-block'}>
                <Input
                  name={'email'}
                  placeholder={'Email'}
                  variant={'email'}
                />
              </div>

              <div className={'form-block'}>
                <Input
                  name={'password'}
                  type={'password'}
                  placeholder={'Password'}
                  variant={'pass'}
                  passValidators={passValidators}
                  tip
                  tooltip
                />
              </div>

              {showCaptcha && <Captcha/>}

              <div className={cn([
                'form-agree',
                {'form-agree_isInvalid': !agreeCheck}
              ])}>
                <Field>
                  {({form, field}: any) => (
                    <input
                      name={'agreement'}
                      id={'agreeInput'}
                      type="checkbox"
                      checked={field.value.agreement}
                      onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                        await form.setFieldValue('agreement', e.target.checked)
                        form.setFieldTouched('agreement', true, false)
                      }}
                    />
                  )}
                </Field>

                <label htmlFor={'agreeInput'}/>

                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '5px' }}>
                  <span>{getLocalizeString('I agree with')}</span>
                  <a
                    href={`https://surfe.${(appType === 'pro') ? 'pro' : 'be'}/site/terms-of-service`}
                    target="_blank"
                  >
                    {getLocalizeString('Terms Of Service')}
                  </a>
                  <span>{getLocalizeString('and')}</span>
                  <a
                    href={`https://surfe.${(appType === 'pro') ? 'pro' : 'be'}/site/privacy-policy`}
                    target="_blank"
                  >
                    {getLocalizeString('Privacy Policy')}
                  </a>
                </div>
              </div>


              <div className={cn([
                'form-agree',
                { 'form-agree_isInvalid': !agreeCheckDataProcessing }
              ])}>
                <Field>
                  {({ form, field }: any) => (
                    <input
                      name={'agreementDataProcessing'}
                      id={'agreeInputDataProcessing'}
                      type="checkbox"
                      checked={field.value.agreementDataProcessing}
                      onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                        await form.setFieldValue('agreementDataProcessing', e.target.checked)
                        form.setFieldTouched('agreementDataProcessing', true, false)
                      }}
                    />
                  )}
                </Field>

                <label htmlFor={'agreeInputDataProcessing'} />

                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '5px' }}>
                  <span>Я даю свое согласие на</span>
                  <a
                    href={`https://surfe.${(appType === 'pro') ? 'pro' : 'be'}/docs/legal/agreement-data-processing`}
                    target="_blank"
                  >
                    обработку моих персональных данных
                  </a>
                </div>
              </div>

              <div className={'form-block'}>
                <Button
                  submit
                  text={'Sign up '}
                  type={'blue'}
                  size={(appType === 'pro') ? 'big' : ''}
                  isLoading={isSubmitting}
                />
              </div>

              {(appType !== 'pro') && (
                <div className={'form-block'}>
                  <Button
                    text={'Already registered? Sign in!'}
                    type={'white'}
                    link={'/login'}
                  />
                </div>
              )}
            </>
          )
        }}
      </Form>
    </>
  )
}

export default Register
